/* eslint-disable no-empty */
import { useCallback, useMemo, useRef, useState } from 'react';

export type BaseFilterType = {
	[key: string]: unknown;
};

/**
 *  @example "status": { "trigger": "Status", "labels": {"draft": "Draft", "active": "Active"} }
 */
export type FriendlyFilterMap = {
	[key: string]: {
		trigger: string;
		labels: {
			[key: string]: string;
		}[];
	};
};

/**
 * Hook matching `useLocalStorage`'s shape so web/mobile can inject their own persistence.
 * When `key` is `undefined`, the value should live in React state only.
 *
 * Web: pass `useLocalStorage`. Mobile: pass an AsyncStorage/MMKV wrapper (or a memory-only hook).
 */
export type PersistedStateHook = <T>(
	key: string | undefined,
	initialValue: T
) => readonly [T | undefined, (value: T) => void, ((...args: never[]) => void)?];

type Props<T extends BaseFilterType> = {
	initialFilters?: Partial<T>;
	requiredFilters?: Partial<T>;
	overrideFilters?: Partial<T>;
	storageKey?: string;
	excludeFilters?: (keyof T)[];
	ignoreFilters?: (keyof T)[];
	usePersistedState: PersistedStateHook;
};

type ReturnType<T extends BaseFilterType> = {
	filters: Partial<T>;
	amountOfActiveFilters: number;
	amountOfActiveFiltersIncludingOverride: number;
	filtersFromStorage?: T;
	applyFilter: (filter: T) => void;
	resetFilter: () => void;
	clearFilters: () => void;
	displayedFilters: string[];
	addFilter: (filterId: string) => void;
	addFilters: (filterIds: string[]) => void;
	removeFilter: (filterId: string) => void;
	removeFilters: (filterIds: string[]) => void;
};

/**
 * Custom hook for managing and applying filters, with injectable persisted storage.
 *
 * @template T - The type of the filter object.
 */
export function useFilterManagement<T extends BaseFilterType>({
	initialFilters = {},
	requiredFilters = {},
	overrideFilters = {},
	ignoreFilters = [],
	storageKey,
	excludeFilters = [],
	usePersistedState,
}: Props<T>): ReturnType<Partial<T>> {
	const [filtersFromStorage, saveFiltersToStorage] = usePersistedState<Partial<T>>(storageKey, {});
	const [displayedFromStorage, saveDisplayedToStorage] = usePersistedState<string[]>(
		storageKey ? `${storageKey}.displayed` : undefined,
		[]
	);

	const initialState = useMemo(() => {
		// When override values (ie URL filters) are present, ignore stored filters to prevent mixing
		// always include the required values
		const hasOverrides =
			Object.keys(overrideFilters).filter(
				(key) => overrideFilters[key] !== null && overrideFilters[key] !== undefined
			).length > 0;
		if (hasOverrides) {
			return {
				...requiredFilters,
				...overrideFilters,
			};
		}
		// then try to read from storage
		if (storageKey && Object.keys(filtersFromStorage || {}).length) {
			return structuredClone({
				...filtersFromStorage,
				...requiredFilters,
			});
		}
		// default is initial + required filters
		return {
			...initialFilters,
			...requiredFilters,
		};
	}, [overrideFilters, initialFilters, storageKey, filtersFromStorage, requiredFilters]);

	const [_storedFilters, _setFilters] = useState<Partial<T>>(initialState || ({} as T));
	const [displayedFilters, setDisplayedFilters] = useState<string[]>(displayedFromStorage ?? []);

	const filters = useMemo(() => {
		return Object.fromEntries(
			Object.entries(_storedFilters).filter(([key]) => !excludeFilters.includes(key as keyof T))
		) as Partial<Partial<T>>;
	}, [_storedFilters, excludeFilters]);

	const amountOfActiveFiltersIncludingOverride = Object.keys(filters || {}).reduce(
		// Check if storage contains any filters, this way we only count the active applied ones
		(acc, key) => (filters?.[key] && !excludeFilters.includes(key) && !ignoreFilters.includes(key) ? acc + 1 : acc),
		0
	);

	const amountOfActiveFiltersExcludingOverrideAndRequired = Object.keys(filters || {}).reduce(
		// Check if storage contains any filters, this way we only count the active applied ones
		(acc, key) =>
			filters?.[key] &&
			!(key in overrideFilters) &&
			!(key in requiredFilters) &&
			!excludeFilters.includes(key) &&
			!ignoreFilters.includes(key)
				? acc + 1
				: acc,
		0
	);
	const hasValidOverrideFilters = useMemo(() => {
		return (
			Object.keys(overrideFilters).filter(
				(key) => overrideFilters[key] !== null && overrideFilters[key] !== undefined
			).length > 0
		);
	}, [overrideFilters]);
	const hasValidRequiredFilters = useMemo(() => {
		return (
			Object.keys(requiredFilters).filter(
				(key) => requiredFilters[key] !== null && requiredFilters[key] !== undefined
			).length > 0
		);
	}, [requiredFilters]);
	const amountOfActiveFilters =
		hasValidOverrideFilters || hasValidRequiredFilters
			? amountOfActiveFiltersExcludingOverrideAndRequired
			: amountOfActiveFiltersIncludingOverride;

	/**
	 * Everything the callbacks below read, refreshed each render.
	 *
	 * These callbacks are imperative actions that consumers place in effect dependency arrays and
	 * memo inputs, so their identity must never change — an unstable `applyFilter` defeated the
	 * `useMemo` around `Overview.context`'s value in both apps, and drove an unbounded re-render in
	 * the booking portal's equipment sidebar, where an effect depended on it and also called it.
	 *
	 * Reading through a ref rather than listing these as dependencies keeps the identities stable
	 * even when a caller passes a fresh object literal, while still acting on the latest values.
	 */
	const latest = useRef({
		storageKey,
		requiredFilters,
		initialFilters,
		ignoreFilters,
		filtersFromStorage,
		_storedFilters,
		saveFiltersToStorage,
		saveDisplayedToStorage,
	});
	latest.current = {
		storageKey,
		requiredFilters,
		initialFilters,
		ignoreFilters,
		filtersFromStorage,
		_storedFilters,
		saveFiltersToStorage,
		saveDisplayedToStorage,
	};

	const applyFilter = useCallback((filter: Partial<T>) => {
		const { storageKey: key, requiredFilters: required, saveFiltersToStorage: save } = latest.current;
		try {
			// Save the filters to storage if required
			if (key) save(filter);
			// Set the filter in state
			_setFilters({
				...filter,
				...required,
			});
		} catch (_) {}
	}, []);

	const resetFilter = useCallback(() => {
		const { filtersFromStorage: stored, initialFilters: initial, requiredFilters: required } = latest.current;
		try {
			const storageIsEmpty = !stored || Object.keys(stored).length === 0;

			_setFilters(
				storageIsEmpty
					? {
							...initial,
							...required,
						}
					: { ...stored, ...required }
			);
		} catch (_) {}
	}, []);

	const clearFilters = useCallback(() => {
		const { _storedFilters: current, ignoreFilters: ignored } = latest.current;
		// Preserve ignored fields' current values — they aren't real, clearable filters (e.g. a
		// header-tab selection persisted alongside the filter set), so "clear all" shouldn't reset them.
		const preserved = Object.fromEntries(
			Object.entries(current).filter(([key]) => ignored.includes(key as keyof T))
		) as Partial<T>;
		applyFilter(preserved);
	}, [applyFilter]);

	const addFilter = useCallback((filterId: string) => {
		setDisplayedFilters((prevFilters) => {
			if (prevFilters.includes(filterId)) {
				return prevFilters;
			}
			const updatedFilters = [...prevFilters, filterId];
			latest.current.saveDisplayedToStorage(updatedFilters);
			return updatedFilters;
		});
	}, []);

	const addFilters = useCallback((filterIds: string[]) => {
		setDisplayedFilters((prevFilters) => {
			const newFilters = filterIds.filter((filterId) => !prevFilters.includes(filterId));
			if (newFilters.length === 0) {
				return prevFilters;
			}
			const updatedFilters = [...prevFilters, ...newFilters];
			latest.current.saveDisplayedToStorage(updatedFilters);
			return updatedFilters;
		});
	}, []);

	const removeFilter = useCallback((filterId: string) => {
		setDisplayedFilters((prevFilters) => {
			const updatedFilters = prevFilters.filter((filter) => filter !== filterId);
			latest.current.saveDisplayedToStorage(updatedFilters);
			return updatedFilters;
		});
	}, []);

	const removeFilters = useCallback((filterIds: string[]) => {
		const filterIdsSet = new Set(filterIds);
		setDisplayedFilters((prevFilters) => {
			const updatedFilters = prevFilters.filter((filter) => !filterIdsSet.has(filter));
			latest.current.saveDisplayedToStorage(updatedFilters);
			return updatedFilters;
		});
	}, []);

	return {
		filters,
		amountOfActiveFilters,
		amountOfActiveFiltersIncludingOverride,
		filtersFromStorage,
		applyFilter,
		resetFilter,
		clearFilters,
		displayedFilters,
		addFilter,
		addFilters,
		removeFilter,
		removeFilters,
	} as const;
}
